import NiceModal from "@ebay/nice-modal-react";
import { Typography } from "@mui/material";
import { GetLifeDetailV2Response, ProductClasType, ProductNameType } from "@repo/api";
import { FC, useCallback, useMemo } from "react";
import { Icons, Modals } from "..";
import { capitalizeFirstLetter, formatDate } from "../../helpers";
import { generateAccordionsSkeletonRows } from "../../helpers/SkeletonGenerator";
import { useIsDesktop, useTypedTranslation } from "../../hooks";
import { Accordions } from "../Accordions";
import { AccordionsMobileSlideUp, AccordionsMobileSlideUpProps } from "../AccordionsMobileSlideUp";
import { PersonAccordionTitle } from "../PersonsAccordion/PersonAccordionTitle";
import { useFufiCoverageNameFormatter } from "./InsuranceCoverage.helpers";
import * as Styled from "./InsuranceCoverageAccordion.styled";
import { InsuranceCoverageBeneficiariesModalBody } from "./InsuranceCoverageBeneficiariesModalBody";
import { InsuranceCoverageDesktopBodyContent } from "./InsuranceCoverageDesktopBodyContent";
import { InsuranceCoverageDetail } from "./InsuranceCoverageDetail";

const TextModalContent: FC<{ text: string }> = ({ text }) => <Typography>{text}</Typography>;

type InsuranceCoverageAccordionProps = {
    isLoading?: boolean;
    helpBox?: React.ReactNode;
    productClass?: ProductClasType;
    data?: GetLifeDetailV2Response;
    productName?: ProductNameType | null;
};

export const InsuranceCoverageAccordion: FC<InsuranceCoverageAccordionProps> = ({
    isLoading,
    helpBox,
    productClass,
    data,
    productName,
}) => {
    const { t } = useTypedTranslation();
    const isDesktop = useIsDesktop();
    const coverageNameFormatter = useFufiCoverageNameFormatter();
    const isUlInitialUnits = productName === ProductNameType.NnFufiUl;

    const handleShowTooltip = useCallback(
        (title: string, text: string) => {
            NiceModal.show(Modals.ModalConfirm, {
                title: title,
                content: TextModalContent,
                additionalContentProps: { text },
                confirmButtonText: t("global.iUnderstand"),
            });
        },
        [t],
    );

    const rows = useMemo<
        {
            id: string;
            TitleContent: React.JSX.Element;
            Bodycontent: React.JSX.Element;
            MobileContent: {
                rows: AccordionsMobileSlideUpProps["accordions"][number]["rows"];
            };
        }[]
    >(() => {
        return (
            data?.insuredPersons.map((person) => {
                const isOwner = data?.owner?.id === person.person.id;
                const role = isOwner
                    ? t("life.global.ownerAndInsuredPerson")
                    : capitalizeFirstLetter(t("life.global.insuredPerson"));
                const name =
                    `${person.person.titlePrefix ?? ""} ${person.person.firstName ?? ""} ${person.person.lastName ?? ""} ${person.person.titleSuffix ?? ""}`.trim();
                const birthDateFormatted = formatDate(person.person.birthDate, "-");

                // filter only active coverages and sort them
                const activeCoverages = (isUlInitialUnits ? person.coverages : person.coverages.filter((coverage) => coverage.isActive))
                    .slice()
                    .sort((a, b) => a.order - b.order);

                return {
                    id: person.person.id.toString(),
                    TitleContent: <PersonAccordionTitle name={name} birthDate={birthDateFormatted} role={role} />,
                    Bodycontent: (
                        <InsuranceCoverageDesktopBodyContent
                            person={{ ...person, coverages: activeCoverages }}
                            productClass={productClass}
                            insuranceTechnicalStartDate={data.technicalStartDate}
                            isOwner={isOwner}
                            productName={productName}
                        />
                    ),
                    MobileContent: {
                        rows: [
                            ...activeCoverages.map((coverage) => ({
                                title: (
                                    <Styled.InsuranceCoverageMobileLinkWrapperStack>
                                        <Styled.InsuranceCoverageMobileLinkTitle>
                                            {coverageNameFormatter(coverage, isOwner, productClass, productName)}
                                        </Styled.InsuranceCoverageMobileLinkTitle>
                                    </Styled.InsuranceCoverageMobileLinkWrapperStack>
                                ),
                                popUpContent: (
                                    <InsuranceCoverageDetail
                                        coverage={coverage}
                                        handleShowTooltip={handleShowTooltip}
                                        productClass={productClass}
                                        insuranceTechnicalStartDate={data.technicalStartDate}
                                        productName={productName}
                                    />
                                ),
                                popUpContentTitle: name,
                            })),
                            {
                                title: (
                                    <Styled.InsuranceCoverageMobileBeneficiariesWrapperStack>
                                        <Styled.InsuranceCoverageMobileOrangeBodyTextM>
                                            {t("life.contractDetail.coverags.beneficiaries")}
                                        </Styled.InsuranceCoverageMobileOrangeBodyTextM>

                                        <Styled.InfoListItemButton disableRipple>
                                            <Icons.Info />
                                        </Styled.InfoListItemButton>
                                    </Styled.InsuranceCoverageMobileBeneficiariesWrapperStack>
                                ),
                                popUpContent: <InsuranceCoverageBeneficiariesModalBody person={person} />,
                                popUpContentTitle: name,
                            },
                        ],
                    },
                };
            }) ?? []
        );
    }, [
        data?.insuredPersons,
        data?.owner?.id,
        t,
        productClass,
        isUlInitialUnits,
        coverageNameFormatter,
        handleShowTooltip,
        data?.technicalStartDate,
    ]);

    const accordions = useMemo(() => {
        return isDesktop ? (
            <Accordions rows={rows ?? []} />
        ) : (
            <AccordionsMobileSlideUp
                accordions={rows.map((accordion) => ({
                    title: accordion.TitleContent,
                    rows: accordion.MobileContent.rows?.map((row) => ({
                        title: row.title,
                        popUpContent: row.popUpContent,
                        popUpContentTitle: row.popUpContentTitle,
                    })),
                }))}
            />
        );
    }, [isDesktop, rows]);

    return (
        <>
            {isLoading ? <Accordions rows={generateAccordionsSkeletonRows(1)} /> : accordions}
            {helpBox}
        </>
    );
};
